import {
  Group2d,
  HandleSnapGeometry,
  SVGContainer,
  ShapeUtil,
  TLHandle,
  TLHandleDragInfo,
  TLLineShape,
  TLLineShapePoint,
  TLResizeInfo,
  Vec,
  WeakCache,
  ZERO_INDEX_KEY,
  getIndexAbove,
  getIndexBetween,
  getIndices,
  lerp,
  maybeSnapToGrid,
  sortByIndex,
} from "tldraw"
import { STROKE_SIZES, useDefaultColorTheme } from "tldraw"
import { mapObjectMapValues } from "../upstream/object"
import {
  PathBuilderGeometry2d,
  PathBuilder,
} from "./draw/pathBuilder/PathBuilder"
import { LiveDraw } from "../LiveDraw"
import {
  LDLineShape,
  lineShapeMigrations,
  lineShapeProps,
} from "../../shared/LDLineShape"
import { useCurrentPage } from "../hooks/useCurrentPage"
import { Expr } from "../../shared/Expr"
import { LDInstanceMeta } from "../../shared/LDInstance"
import { LDPage } from "../../shared/LDPage"
import { assert } from "../upstream/control"

const handlesCache = new WeakCache<LDLineShape["props"], TLHandle[]>()

/** @public */
export class LDLineShapeUtil extends ShapeUtil<LDLineShape> {
  static override type = "custom-line" as const
  static override props = lineShapeProps
  static override migrations = lineShapeMigrations

  override canTabTo() {
    return false
  }
  override hideResizeHandles() {
    return true
  }
  override hideRotateHandle() {
    return true
  }
  override hideSelectionBoundsFg() {
    return true
  }
  override hideSelectionBoundsBg() {
    return true
  }

  override getDefaultProps(): LDLineShape["props"] {
    const [start, end] = getIndices(2)
    const instanceMeta = this.editor.getInstanceState().meta as LDInstanceMeta
    return {
      dash: "draw",
      spline: "line",
      points: {
        [start]: { id: start, index: start, x: 0, y: 0 },
        [end]: { id: end, index: end, x: 0.1, y: 0.1 },
      },
      scale: 1,
      layer: "layer1",
      parameterValues: {
        strokeColor: Expr.lit(instanceMeta.strokeColor),
        strokeWidth: Expr.lit(instanceMeta.strokeWidth),
      },
    }
  }

  getGeometry(shape: LDLineShape) {
    // todo: should we have min size?
    const geometry = getPathForLineShape(shape).toGeometry()
    assert(geometry instanceof PathBuilderGeometry2d)
    return geometry
  }

  override getHandles(shape: LDLineShape) {
    return handlesCache.get(shape.props, () => {
      const spline = this.getGeometry(shape)

      const points = linePointsToArray(shape)
      const results: TLHandle[] = points.map((point) => ({
        ...point,
        id: point.index,
        type: "vertex",
        canSnap: true,
      }))

      // Alex - Remove middle handle in line shape (for now at least)

      // for (let i = 0; i < points.length - 1; i++) {
      //   const index = getIndexBetween(points[i].index, points[i + 1].index)
      //   const segment = spline.getSegments()[i]
      //   const point = segment.interpolateAlongEdge(0.5)
      //   // results.push({
      //   //   id: index,
      //   //   type: "create",
      //   //   index,
      //   //   x: point.x,
      //   //   y: point.y,
      //   //   canSnap: true,
      //   // })
      // }

      return results.sort(sortByIndex)
    })
  }

  //   Events

  override onResize(shape: LDLineShape, info: TLResizeInfo<LDLineShape>) {
    const { scaleX, scaleY } = info

    return {
      props: {
        points: mapObjectMapValues(
          shape.props.points,
          (_, { id, index, x, y }) => ({
            id,
            index,
            x: x * scaleX,
            y: y * scaleY,
          }),
        ),
      },
    }
  }

  override onBeforeCreate(next: LDLineShape): void | LDLineShape {
    const {
      props: { points },
    } = next
    const pointKeys = Object.keys(points)

    if (pointKeys.length < 2) {
      return
    }

    const firstPoint = points[pointKeys[0]]
    const allSame = pointKeys.every((key) => {
      const point = points[key]
      return point.x === firstPoint.x && point.y === firstPoint.y
    })
    if (allSame) {
      const lastKey = pointKeys[pointKeys.length - 1]
      points[lastKey] = {
        ...points[lastKey],
        x: points[lastKey].x + 0.1,
        y: points[lastKey].y + 0.1,
      }
      return next
    }
    return
  }

  override onHandleDrag(
    shape: LDLineShape,
    { handle }: TLHandleDragInfo<LDLineShape>,
  ) {
    // we should only ever be dragging vertex handles
    if (handle.type !== "vertex") return
    const newPoint = maybeSnapToGrid(new Vec(handle.x, handle.y), this.editor)
    return {
      ...shape,
      props: {
        ...shape.props,
        points: {
          ...shape.props.points,
          [handle.id]: {
            id: handle.id,
            index: handle.index,
            x: newPoint.x,
            y: newPoint.y,
          },
        },
      },
    }
  }

  component(shape: LDLineShape) {
    return (
      <SVGContainer style={{ minWidth: 50, minHeight: 50 }}>
        <LineShapeSvg shape={shape} />
      </SVGContainer>
    )
  }

  indicator(shape: LDLineShape) {
    const page = this.editor.getCurrentPage() as LDPage
    const strokeWidth =
      Expr.evaluate(
        shape.props.parameterValues.strokeWidth,
        page.meta.parameterValues,
      ) * shape.props.scale
    const path = getPathForLineShape(shape)
    const { dash } = shape.props

    return path.toSvg({
      style: dash === "draw" ? "draw" : "solid",
      strokeWidth: 1,
      passes: 1,
      randomSeed: shape.id,
      offset: 0,
      roundness: strokeWidth * 2,
      props: { strokeWidth: undefined },
    })
  }

  override toSvg(shape: LDLineShape) {
    return <LineShapeSvg shouldScale shape={shape} />
  }

  override getHandleSnapGeometry(shape: LDLineShape): HandleSnapGeometry {
    const points = linePointsToArray(shape)
    return {
      points,
      getSelfSnapPoints: (handle) => {
        const index = this.getHandles(shape)
          .filter((h) => h.type === "vertex")
          .findIndex((h) => h.id === handle.id)!

        // We want to skip the current and adjacent handles
        return points.filter((_, i) => Math.abs(i - index) > 1).map(Vec.From)
      },
      getSelfSnapOutline: (handle) => {
        // We want to skip the segments that include the handle, so
        // find the index of the handle that shares the same index property
        // as the initial dragging handle; this catches a quirk of create handles
        const index = this.getHandles(shape)
          .filter((h) => h.type === "vertex")
          .findIndex((h) => h.id === handle.id)!

        // Get all the outline segments from the shape that don't include the handle
        const segments = this.getGeometry(shape)
          .getSegments()
          .filter((_, i) => i !== index - 1 && i !== index)

        if (!segments.length) return null
        return new Group2d({ children: segments })
      },
    }
  }
  override getInterpolatedProps(
    startShape: LDLineShape,
    endShape: LDLineShape,
    t: number,
  ): LDLineShape["props"] {
    const startPoints = linePointsToArray(startShape)
    const endPoints = linePointsToArray(endShape)

    const pointsToUseStart: TLLineShapePoint[] = []
    const pointsToUseEnd: TLLineShapePoint[] = []

    let index = ZERO_INDEX_KEY

    if (startPoints.length > endPoints.length) {
      // we'll need to expand points
      for (let i = 0; i < startPoints.length; i++) {
        pointsToUseStart[i] = { ...startPoints[i] }
        if (endPoints[i] === undefined) {
          pointsToUseEnd[i] = { ...endPoints[endPoints.length - 1], id: index }
        } else {
          pointsToUseEnd[i] = { ...endPoints[i], id: index }
        }
        index = getIndexAbove(index)
      }
    } else if (endPoints.length > startPoints.length) {
      // we'll need to converge points
      for (let i = 0; i < endPoints.length; i++) {
        pointsToUseEnd[i] = { ...endPoints[i] }
        if (startPoints[i] === undefined) {
          pointsToUseStart[i] = {
            ...startPoints[startPoints.length - 1],
            id: index,
          }
        } else {
          pointsToUseStart[i] = { ...startPoints[i], id: index }
        }
        index = getIndexAbove(index)
      }
    } else {
      // noop, easy
      for (let i = 0; i < endPoints.length; i++) {
        pointsToUseStart[i] = startPoints[i]
        pointsToUseEnd[i] = endPoints[i]
      }
    }

    return {
      ...(t > 0.5 ? endShape.props : startShape.props),
      points: Object.fromEntries(
        pointsToUseStart.map((point, i) => {
          const endPoint = pointsToUseEnd[i]
          return [
            point.id,
            {
              ...point,
              x: lerp(point.x, endPoint.x, t),
              y: lerp(point.y, endPoint.y, t),
            },
          ]
        }),
      ),
      scale: lerp(startShape.props.scale, endShape.props.scale, t),
    }
  }
}

function linePointsToArray(shape: LDLineShape) {
  return Object.values(shape.props.points).sort(sortByIndex)
}

const pathCache = new WeakCache<LDLineShape, PathBuilder>()
function getPathForLineShape(shape: LDLineShape): PathBuilder {
  return pathCache.get(shape, () => {
    const points = linePointsToArray(shape).map(Vec.From)

    switch (shape.props.spline) {
      case "cubic": {
        return PathBuilder.cubicSplineThroughPoints(points, { endOffsets: 0 })
      }
      case "line": {
        return PathBuilder.lineThroughPoints(points, { endOffsets: 0 })
      }
    }
  })
}

function LineShapeSvg({
  shape,
  shouldScale = false,
  forceSolid = false,
}: {
  shape: LDLineShape
  shouldScale?: boolean
  forceSolid?: boolean
}) {
  const theme = useDefaultColorTheme()

  const path = getPathForLineShape(shape)
  const { dash, layer: layerId } = shape.props
  const page = useCurrentPage()
  const layer = page.meta.layers[layerId]

  const scaleFactor = 1 / shape.props.scale

  const scale = shouldScale ? scaleFactor : 1

  const strokeWidth = Expr.evaluate(
    shape.props.parameterValues.strokeWidth,
    page.meta.parameterValues,
  )

  return path.toSvg({
    style: dash,
    strokeWidth,
    forceSolid,
    randomSeed: shape.id,
    props: {
      transform: `scale(${scale})`,
      stroke: Expr.evaluate(
        shape.props.parameterValues.strokeColor,
        page.meta.parameterValues,
      ),
      fill: "none",
    },
  })
}
